import { OWL_LISTING_ROW_COUNT } from 'maplestory-owl-transcribe-contract';
import { FREDRICK_DERIVED_SCHEMA_VERSION, expectedOwlPageCount, isSourceImageId, } from './index.js';
export function derivedSearchIsStructurallyComplete(search) {
    const expected = expectedOwlPageCount(search.totalResults);
    if (search.presentPages.length !== expected)
        return false;
    const present = new Set();
    for (const page of search.presentPages) {
        if (!Number.isSafeInteger(page) || page < 1 || page > expected) {
            return false;
        }
        if (present.has(page))
            return false;
        present.add(page);
    }
    return present.size === expected;
}
function record(value, path) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        throw new TypeError(`${path} must be an object.`);
    }
    return value;
}
function exactKeys(value, path, required) {
    const allowed = new Set(required);
    for (const key of required) {
        if (!Object.hasOwn(value, key))
            throw new TypeError(`${path}.${key} is required.`);
    }
    for (const key of Object.keys(value)) {
        if (!allowed.has(key))
            throw new TypeError(`${path}.${key} is not a field in the current derived schema.`);
    }
}
function stringValue(value, path) {
    if (typeof value !== 'string' || value.length === 0) {
        throw new TypeError(`${path} must be a non-empty string.`);
    }
    return value;
}
function integer(value, path, minimum = 0) {
    if (!Number.isSafeInteger(value) || value < minimum) {
        throw new TypeError(`${path} must be a safe integer greater than or equal to ${minimum}.`);
    }
    return value;
}
function finiteNumber(value, path, minimum = 0) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum) {
        throw new TypeError(`${path} must be a finite number greater than or equal to ${minimum}.`);
    }
    return value;
}
function stringArray(value, path) {
    if (!Array.isArray(value))
        throw new TypeError(`${path} must be an array.`);
    return value.map((entry, index) => stringValue(entry, `${path}[${index}]`));
}
function integerArray(value, path, minimum = 0) {
    if (!Array.isArray(value))
        throw new TypeError(`${path} must be an array.`);
    return value.map((entry, index) => integer(entry, `${path}[${index}]`, minimum));
}
function assertSortedUniqueStrings(values, path) {
    for (let index = 0; index < values.length; index += 1) {
        if (index > 0 && values[index - 1] >= values[index]) {
            throw new TypeError(`${path} must be strictly sorted and unique.`);
        }
    }
}
function assertSortedUniqueIntegers(values, path) {
    for (let index = 0; index < values.length; index += 1) {
        if (index > 0 && values[index - 1] >= values[index]) {
            throw new TypeError(`${path} must be strictly sorted and unique.`);
        }
    }
}
function groupingState(value, path) {
    if (value !== 'high-confidence' && value !== 'ambiguous') {
        throw new TypeError(`${path} must be a supported grouping state.`);
    }
    return value;
}
function completeness(value, path) {
    if (value !== 'complete' && value !== 'partial') {
        throw new TypeError(`${path} must be a supported completeness state.`);
    }
    return value;
}
function schemaVersion(value, path) {
    if (value !== FREDRICK_DERIVED_SCHEMA_VERSION) {
        throw new TypeError(`${path} must equal ${FREDRICK_DERIVED_SCHEMA_VERSION}.`);
    }
    return FREDRICK_DERIVED_SCHEMA_VERSION;
}
function parsePrices(value, path) {
    if (value === null)
        return null;
    const input = record(value, path);
    exactKeys(input, path, [
        'count', 'min', 'p10', 'p25', 'median', 'p75', 'p90', 'max', 'mean',
        'populationStandardDeviation',
    ]);
    const result = {
        count: integer(input.count, `${path}.count`, 1),
        min: finiteNumber(input.min, `${path}.min`),
        p10: finiteNumber(input.p10, `${path}.p10`),
        p25: finiteNumber(input.p25, `${path}.p25`),
        median: finiteNumber(input.median, `${path}.median`),
        p75: finiteNumber(input.p75, `${path}.p75`),
        p90: finiteNumber(input.p90, `${path}.p90`),
        max: finiteNumber(input.max, `${path}.max`),
        mean: finiteNumber(input.mean, `${path}.mean`),
        populationStandardDeviation: finiteNumber(input.populationStandardDeviation, `${path}.populationStandardDeviation`),
    };
    if (!(result.min <= result.p10
        && result.p10 <= result.p25
        && result.p25 <= result.median
        && result.median <= result.p75
        && result.p75 <= result.p90
        && result.p90 <= result.max)) {
        throw new TypeError(`${path} percentiles must be monotonic.`);
    }
    return result;
}
export function parseFredrickDerivedSearchV1(value) {
    const path = 'derivedSearch';
    const input = record(value, path);
    exactKeys(input, path, [
        'schemaVersion', 'searchId', 'processorVersion', 'marketVariant', 'sourceImageIds', 'revisionIds',
        'itemName', 'itemKey', 'totalResults', 'observedAtStart', 'observedAtEnd',
        'expectedPageCount', 'presentPages', 'completeness', 'groupingState',
    ]);
    const result = {
        schemaVersion: schemaVersion(input.schemaVersion, `${path}.schemaVersion`),
        searchId: stringValue(input.searchId, `${path}.searchId`),
        processorVersion: stringValue(input.processorVersion, `${path}.processorVersion`),
        marketVariant: stringValue(input.marketVariant, `${path}.marketVariant`),
        sourceImageIds: stringArray(input.sourceImageIds, `${path}.sourceImageIds`),
        revisionIds: stringArray(input.revisionIds, `${path}.revisionIds`),
        itemName: stringValue(input.itemName, `${path}.itemName`),
        itemKey: stringValue(input.itemKey, `${path}.itemKey`),
        totalResults: integer(input.totalResults, `${path}.totalResults`),
        observedAtStart: integer(input.observedAtStart, `${path}.observedAtStart`),
        observedAtEnd: integer(input.observedAtEnd, `${path}.observedAtEnd`),
        expectedPageCount: integer(input.expectedPageCount, `${path}.expectedPageCount`, 1),
        presentPages: integerArray(input.presentPages, `${path}.presentPages`, 1),
        completeness: completeness(input.completeness, `${path}.completeness`),
        groupingState: groupingState(input.groupingState, `${path}.groupingState`),
    };
    if (result.sourceImageIds.length === 0
        || result.sourceImageIds.length !== result.revisionIds.length) {
        throw new TypeError(`${path} must contain one source and revision ID per selected page.`);
    }
    if (result.sourceImageIds.some((value) => !isSourceImageId(value))) {
        throw new TypeError(`${path}.sourceImageIds contains an invalid source image ID.`);
    }
    assertSortedUniqueStrings(result.sourceImageIds, `${path}.sourceImageIds`);
    assertSortedUniqueStrings(result.revisionIds, `${path}.revisionIds`);
    assertSortedUniqueIntegers(result.presentPages, `${path}.presentPages`);
    if (result.observedAtEnd < result.observedAtStart) {
        throw new TypeError(`${path}.observedAtEnd must not precede observedAtStart.`);
    }
    if (result.expectedPageCount !== expectedOwlPageCount(result.totalResults)) {
        throw new TypeError(`${path}.expectedPageCount is inconsistent with totalResults.`);
    }
    const structurallyComplete = derivedSearchIsStructurallyComplete(result);
    if ((result.completeness === 'complete') !== structurallyComplete) {
        throw new TypeError(`${path}.completeness is inconsistent with presentPages.`);
    }
    return result;
}
export function parseFredrickDerivedSearchPageV1(value) {
    const path = 'derivedSearchPage';
    const input = record(value, path);
    exactKeys(input, path, ['id', 'searchId', 'sourceImageId', 'revisionId', 'page']);
    const sourceImageId = stringValue(input.sourceImageId, `${path}.sourceImageId`);
    if (!isSourceImageId(sourceImageId)) {
        throw new TypeError(`${path}.sourceImageId is invalid.`);
    }
    return {
        id: stringValue(input.id, `${path}.id`),
        searchId: stringValue(input.searchId, `${path}.searchId`),
        sourceImageId,
        revisionId: stringValue(input.revisionId, `${path}.revisionId`),
        page: integer(input.page, `${path}.page`, 1),
    };
}
export function parseFredrickDerivedListingV1(value) {
    const path = 'derivedListing';
    const input = record(value, path);
    exactKeys(input, path, [
        'id', 'searchId', 'sourceImageId', 'revisionId', 'page', 'itemName', 'itemKey',
        'observedAt', 'marketVariant', 'storeNameVerified', 'rowIndex', 'sellerName',
        'storeName', 'price', 'units',
    ]);
    const sellerName = stringValue(input.sellerName, `${path}.sellerName`);
    const storeName = input.storeName === null
        ? null
        : stringValue(input.storeName, `${path}.storeName`);
    if (input.storeNameVerified !== false) {
        throw new TypeError(`${path}.storeNameVerified must be false.`);
    }
    const sourceImageId = stringValue(input.sourceImageId, `${path}.sourceImageId`);
    if (!isSourceImageId(sourceImageId)) {
        throw new TypeError(`${path}.sourceImageId is invalid.`);
    }
    const rowIndex = integer(input.rowIndex, `${path}.rowIndex`);
    if (rowIndex >= OWL_LISTING_ROW_COUNT) {
        throw new TypeError(`${path}.rowIndex must be below ${OWL_LISTING_ROW_COUNT}.`);
    }
    return {
        id: stringValue(input.id, `${path}.id`),
        searchId: stringValue(input.searchId, `${path}.searchId`),
        sourceImageId,
        revisionId: stringValue(input.revisionId, `${path}.revisionId`),
        page: integer(input.page, `${path}.page`, 1),
        itemName: stringValue(input.itemName, `${path}.itemName`),
        itemKey: stringValue(input.itemKey, `${path}.itemKey`),
        observedAt: integer(input.observedAt, `${path}.observedAt`),
        marketVariant: stringValue(input.marketVariant, `${path}.marketVariant`),
        storeNameVerified: false,
        rowIndex,
        sellerName,
        storeName,
        price: integer(input.price, `${path}.price`),
        units: integer(input.units, `${path}.units`, 1),
    };
}
export function parseFredrickDerivedSearchSummaryV1(value) {
    const path = 'derivedSearchSummary';
    const input = record(value, path);
    exactKeys(input, path, [
        'schemaVersion', 'searchId', 'processorVersion', 'statisticsVersion', 'marketVariant', 'itemName',
        'itemKey', 'observedAt', 'observedAtEnd', 'totalResults', 'listingCount',
        'totalUnits', 'completeness', 'groupingState', 'prices',
    ]);
    const listingCount = integer(input.listingCount, `${path}.listingCount`);
    const prices = parsePrices(input.prices, `${path}.prices`);
    if ((prices?.count ?? 0) !== listingCount) {
        throw new TypeError(`${path}.prices.count must equal listingCount.`);
    }
    const result = {
        schemaVersion: schemaVersion(input.schemaVersion, `${path}.schemaVersion`),
        searchId: stringValue(input.searchId, `${path}.searchId`),
        processorVersion: stringValue(input.processorVersion, `${path}.processorVersion`),
        statisticsVersion: stringValue(input.statisticsVersion, `${path}.statisticsVersion`),
        marketVariant: stringValue(input.marketVariant, `${path}.marketVariant`),
        itemName: stringValue(input.itemName, `${path}.itemName`),
        itemKey: stringValue(input.itemKey, `${path}.itemKey`),
        observedAt: integer(input.observedAt, `${path}.observedAt`),
        observedAtEnd: integer(input.observedAtEnd, `${path}.observedAtEnd`),
        totalResults: integer(input.totalResults, `${path}.totalResults`),
        listingCount,
        totalUnits: integer(input.totalUnits, `${path}.totalUnits`),
        completeness: completeness(input.completeness, `${path}.completeness`),
        groupingState: groupingState(input.groupingState, `${path}.groupingState`),
        prices,
    };
    if (result.observedAtEnd < result.observedAt) {
        throw new TypeError(`${path}.observedAtEnd must not precede observedAt.`);
    }
    return result;
}
export function parseFredrickDerivedItemLatestV1(value) {
    const path = 'derivedItemLatest';
    const input = record(value, path);
    exactKeys(input, path, [
        'schemaVersion', 'itemKey', 'itemName', 'searchId', 'processorVersion',
        'statisticsVersion', 'marketVariant', 'observedAt', 'observedAtEnd', 'totalResults',
        'listingCount', 'totalUnits', 'completeness', 'groupingState', 'prices',
    ]);
    if (input.completeness !== 'complete') {
        throw new TypeError(`${path}.completeness must be complete.`);
    }
    if (input.groupingState !== 'high-confidence') {
        throw new TypeError(`${path}.groupingState must be high-confidence.`);
    }
    const summary = parseFredrickDerivedSearchSummaryV1({
        ...input,
        completeness: 'complete',
        groupingState: 'high-confidence',
    });
    return {
        ...summary,
        completeness: 'complete',
        groupingState: 'high-confidence',
    };
}
const claimDiagnosticStatuses = new Set([
    'accepted-head', 'accepted-ancestor', 'invalid-structure', 'inapplicable',
    'ambiguous-branch', 'source-ownership-conflict', 'not-selected',
]);
const claimDiagnosticReasons = new Set([
    'invalid-claim', 'invalid-root', 'invalid-parent', 'not-strict-superset',
    'source-not-market-eligible', 'incompatible-sources', 'duplicate-page',
    'changed-parent-page', 'multiple-applicable-children',
    'source-owned-by-multiple-lineages',
]);
export function parseFredrickDerivedClaimDiagnosticV1(value) {
    const path = 'derivedClaimDiagnostic';
    const input = record(value, path);
    const allowed = new Set(['claimId', 'repositoryId', 'searchId', 'status', 'reason']);
    for (const key of ['claimId', 'repositoryId', 'searchId', 'status']) {
        if (!Object.hasOwn(input, key))
            throw new TypeError(`${path}.${key} is required.`);
    }
    for (const key of Object.keys(input)) {
        if (!allowed.has(key))
            throw new TypeError(`${path}.${key} is not a field in the current derived schema.`);
    }
    const status = stringValue(input.status, `${path}.status`);
    if (!claimDiagnosticStatuses.has(status))
        throw new TypeError(`${path}.status is invalid.`);
    const reason = input.reason === undefined
        ? undefined
        : stringValue(input.reason, `${path}.reason`);
    if (reason !== undefined && !claimDiagnosticReasons.has(reason)) {
        throw new TypeError(`${path}.reason is invalid.`);
    }
    return {
        claimId: stringValue(input.claimId, `${path}.claimId`),
        repositoryId: stringValue(input.repositoryId, `${path}.repositoryId`),
        searchId: stringValue(input.searchId, `${path}.searchId`),
        status,
        ...(reason === undefined ? {} : { reason }),
    };
}
export function parseFredrickDerivedLineageBranchV1(value) {
    const path = 'derivedLineageBranch';
    const input = record(value, path);
    exactKeys(input, path, ['id', 'repositoryId', 'searchId', 'parentClaimId', 'childClaimIds']);
    const childClaimIds = stringArray(input.childClaimIds, `${path}.childClaimIds`);
    if (childClaimIds.length < 2)
        throw new TypeError(`${path}.childClaimIds must contain at least two branches.`);
    assertSortedUniqueStrings(childClaimIds, `${path}.childClaimIds`);
    return {
        id: stringValue(input.id, `${path}.id`),
        repositoryId: stringValue(input.repositoryId, `${path}.repositoryId`),
        searchId: stringValue(input.searchId, `${path}.searchId`),
        parentClaimId: stringValue(input.parentClaimId, `${path}.parentClaimId`),
        childClaimIds,
    };
}
//# sourceMappingURL=derived.js.map